import type { DeviceContact } from '@/types/contacts'
import { validatePhoneNumber } from './validatePhoneNumber'

export const phoneNumberUtils = {
  validatePhoneNumber,
}

// Extract phone number from device contact
export const extractPhoneNumberFromContact = (
  phoneNumbers: Array<{ number: string; countryCode?: string }> = [],
): string => {
  if (phoneNumbers.length === 0) return ''

  const primaryNumber = phoneNumbers[0]
  return primaryNumber.number || ''
}

// Clean phone number and format for API
export const cleanPhoneNumber = (phoneNumber: string): string => {
  // Remove all non-digit characters except +
  return phoneNumber.replace(/[^\d+]/g, '')
}

// Format phone number for display
export const formatPhoneNumber = (phoneNumber: string): string => {
  const cleaned = cleanPhoneNumber(phoneNumber)

  // Basic formatting - can be enhanced based on country codes
  if (cleaned.startsWith('+')) {
    return cleaned
  }

  // Add + if not present and looks like international number
  if (cleaned.length > 10) {
    return `+${cleaned}`
  }

  return cleaned
}

// Extract phone numbers from device contacts
export const extractPhoneNumbersFromContacts = (
  contacts: DeviceContact[],
): string[] => {
  return contacts
    .map((contact) => extractPhoneNumberFromContact(contact.phoneNumbers))
    .filter((phone) => phone.length > 0)
    .map((phone) => cleanPhoneNumber(phone))
}

// Add DDI (country code) to phone numbers
export const addDDI = (phoneNumber: string, countryCode = '+965'): string => {
  const cleaned = cleanPhoneNumber(phoneNumber)

  if (cleaned.startsWith('+')) {
    return cleaned
  }

  return `${countryCode}${cleaned}`
}

// Get only numbers with DDI from contacts
export const getOnlyNumbersWithDDI = (contacts: DeviceContact[]): string[] => {
  return extractPhoneNumbersFromContacts(contacts).map((phone) => addDDI(phone))
}

// Validate phone number
export const isValidPhoneNumber = (phoneNumber: string): boolean => {
  const cleaned = cleanPhoneNumber(phoneNumber)

  // Basic validation - at least 7 digits, max 15 digits
  const digitsOnly = cleaned.replace(/^\+/, '')
  return /^\d{7,15}$/.test(digitsOnly)
}

// Format phone number for SMS
export const formatPhoneNumberForSMS = (phoneNumber: string): string => {
  return cleanPhoneNumber(phoneNumber)
}

// Clean number and handle DDI before sending to API
export const cleanNumberAndHandleDDIBeforeSendToAPI = (
  phoneNumber: string,
): string => {
  const cleaned = cleanPhoneNumber(phoneNumber)

  // Ensure it starts with +
  if (!cleaned.startsWith('+')) {
    return `+${cleaned}`
  }

  return cleaned
}

export { extractPhoneNumberInfosWithoutRegionCode } from './extractPhoneNumberInfosWithoutRegionCode'
